import { AlertTriangle, ArrowDownToLine, ArrowUpFromLine, RefreshCw, Timer } from "lucide-react";
import { useEffect, useId, useRef, useState, type ComponentType, type ReactNode } from "react";
import { formatRelativeTime } from "../dateFormatting";
import { useChangeFlash } from "../hooks/useChangeFlash";
import { usePopoverSide, type PopoverRoom, type PopoverSide } from "../hooks/usePopoverSide";
import { useSyncStatus } from "../hooks/useSyncStatus";
import { useLanguage } from "../i18n/language-provider";
import type { PmsSyncStatusOn, SyncIssue, SyncIssueKind } from "../services/syncStatusApi";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { AutoSyncConfig } from "./AutoSyncConfig";
import { OutlookMark, PricingMark } from "./dashboard/OutlookMark";
import { syncCadencePhrase } from "./pms/syncCadence";
import { readingOf, tonightLine, tonightSentences, tonightTitle, tonightTooltipLines } from "./pms/tonightReading";
import { useFrontDeskAccess, useReadOnlyAccess } from "./access/read-only-access";
import { requestOpenPropertyCheck } from "../services/openPropertyEvents";
import { useInsidePhoneMenu } from "./RailFrame";
import { RenderFaultBoundary } from "./RenderFaultBoundary";

/**
 * The popover's room: 24rem wide, as `w-96` draws it, 4px off the chip and
 * 8px from the window's edge. `usePopoverSide` asks whether that fits beside
 * the chip, and Radix keeps the popover that far from the edge wherever it
 * lands.
 */
export const SYNC_POPOVER_ROOM: PopoverRoom = { widthRem: 24, sideOffset: 4, collisionPadding: 8 };

/**
 * The pages the popover's actions lead to. Deliberately not "strategies":
 * the action named after the sync settings used to land there, and that page
 * only shows read-only status cards — the controls live in the auto-sync
 * dialog, which the popover now opens in place.
 *
 * "dashboard" is where the popover's "Today" section goes on: its two
 * sentences are the first row of the dashboard's three nights, and the other
 * nights and the months are that page's to show.
 */
export type SyncStatusDestination = "connections" | "uploadLog" | "dashboard";

interface PmsSyncStatusProps {
    /** Re-fetch when this changes (the active connection switched, or a connection was modified). */
    refreshKey?: unknown;
    /**
     * The property the rail names — the one the tab was loaded for. The
     * verdict is read for the property open now, and the two part ways when
     * the property is switched somewhere this tab cannot see: a verdict about
     * another property is then not drawn, and the shell is asked which one is
     * open (`openPropertyEvents.ts`). The indicator polls a minute apart, so a
     * tab that stays focused while the property is switched on another device
     * hears of it here, from nothing else.
     */
    connectionId?: number | null;
    /** Dot-only variant for the collapsed sidebar rail. */
    collapsed?: boolean;
    /** Where the popover's actions lead. */
    onNavigate?: (page: SyncStatusDestination) => void;
    /**
     * Whether the dashboard is the page behind the popover. The popover then
     * offers no way to it: a button that leads where the reader already is
     * closes the popover and does nothing else.
     */
    dashboardOpen?: boolean;
}

/**
 * How the indicator reads, once the verdict and the auto-send switch are put
 * together. `manual` is not a verdict the server issues — it is `good` with
 * nothing leaving the building, which is a state rather than a fault, and so
 * neither the healthy colour nor an alarming one.
 */
type Tone = "ok" | "muted" | "warn" | "bad" | "busy";

/**
 * The mark is the whole signal: a status indicator is "small colored dots or
 * badges using the status tokens, not large icons", and the tokens are
 * chosen by luminance so the states survive the high-contrast skin and a
 * grayscale print. `busy` has no dot — motion says it, and the standard
 * spinner is the only motion allowed.
 */
const DOT_CLASS: Record<Exclude<Tone, "busy" | "muted">, string> = {
    ok: "bg-success",
    warn: "bg-warning",
    bad: "bg-destructive",
};

/**
 * The rail is dark in both modes and the popover is paper, so the neutral
 * marks take their grey from the surface they sit on; the verdict colours
 * read on both.
 */
type MarkSurface = "rail" | "popover";

const NEUTRAL_CLASS: Record<MarkSurface, { dot: string; spinner: string }> = {
    rail: { dot: "bg-sidebar-foreground/50", spinner: "text-sidebar-foreground/70" },
    popover: { dot: "bg-muted-foreground/50", spinner: "text-muted-foreground" },
};

/** The height of the popover's actions: a full touch target below `sm`, the small button's own from there. */
const POPOVER_ACTION_CLASS = "h-10 sm:h-8";

/** Issues whose fix starts at the connection screen. */
const CONNECTION_ISSUES = new Set<SyncIssue["kind"]>(["lastRunFailed", "pmsUnreachable"]);

type Strings = ReturnType<typeof useLanguage>["t"]["syncStatus"];

interface Reading {
    /** Two or three words: the state, or the fault when there is one. */
    headline: string;
    /**
     * The relative time, which already says which way it runs. Where the page
     * cannot read the state, what brings the page that can.
     */
    subline: string;
    /** One sentence for the popover header. */
    summary: string;
    tone: Tone;
    /**
     * Whether the headline is the first listed issue's own name, which the
     * popover's list then does not print a second time. Absent where the
     * headline is a state: the first issue is then named nowhere but there.
     */
    namesLeadingIssue?: boolean;
}

function fill(template: string, values: Record<string, string>): string {
    return Object.entries(values).reduce(
        (text, [key, value]) => text.split(`{${key}}`).join(value),
        template,
    );
}

/** What an issue is called, and how the popover explains it. */
interface IssueWords {
    /** Two or three words: the rail's headline, and a name in the popover's list. */
    short: string;
    /** A sentence or two, `{provider}` still to be filled in. */
    long: string;
}

/**
 * The words this page has for an issue kind, or null where it has none.
 *
 * `SyncIssueKind` lists the kinds this page was built with, which is not what
 * the server may send: the two deploy separately and a tab stays open for
 * days, so every kind the server gains is met by pages that predate it
 * (`lastPushHeldBack`, 4 Oct 2026). Read straight out of the tables, such a
 * kind came back `undefined`: an empty headline on the rail, and a throw in
 * `fill`. The popover's content is built on every render, open or not, so
 * that throw took down every page the rail is on the moment the status
 * arrived.
 *
 * The tables are still indexed by the typed kind, so the compiler goes on
 * refusing a kind added to the list without its words, and what comes back is
 * read as a string or as nothing. Not with `??`: a kind called `constructor`
 * reads back `Object`.
 */
function issueWords(s: Strings, kind: SyncIssueKind): IssueWords | null {
    const short: unknown = s.issueShort[kind];
    const long: unknown = s.issueLong[kind];
    return typeof short === "string" && typeof long === "string" ? { short, long } : null;
}

/**
 * How the popover explains an issue: in the kind's own words, except where
 * the last upload put a price list back and what the kind says of a send does
 * not hold of a restore.
 *
 * One kind is said both ways. A night Optivo held back means the same
 * whatever started the upload (the PMS refused nothing, and the Upload Log
 * has the reason), so it is one issue under one name. What becomes of the
 * night is not the same. A send leaves it in the draft and a later send tries
 * it again. A restore commits no drafts, so nothing tries it again: it keeps
 * what it has until the price list is restored again or the value is set in
 * the PMS. The status reads the newest upload whatever started it, and the
 * popover promised the retry of a restore too.
 *
 * Every other kind is explained the same of any upload. "The last price
 * update did not reach …" promises nothing a restore does not keep.
 */
function explanationFor(kind: SyncIssueKind, words: IssueWords, s: Strings, lastPushWasRestore: boolean): string {
    return lastPushWasRestore && kind === "lastPushHeldBack" ? s.issueLongRestore.lastPushHeldBack : words.long;
}

/** One entry of the popover's issue list. */
interface IssueRow {
    key: string;
    /** Printed over the explanation, except on the entry the header already names. */
    label: string;
    explanation: string;
    /** The recorded faults, as the server worded them. */
    messages: string[];
}

/**
 * The popover's list: an entry for each issue this page can name, and one for
 * all those it cannot.
 *
 * Of a kind it has no words for, the page can truthfully say only that it
 * cannot describe it and that reloading brings the page that can. It says so
 * once, however many such kinds there are. Each one's recorded fault is still
 * printed: that is the server's own account, in English, and needs no words
 * from this page. The entry stands where the first of them stood, so the
 * server's order (most severe first) is kept and the first entry is still the
 * one the header names, where it names one. No action is offered for it, since
 * nothing says which screen its fix starts at.
 */
function issueRows(issues: SyncIssue[], s: Strings, provider: string, lastPushWasRestore: boolean): IssueRow[] {
    const rows: IssueRow[] = [];
    let unnamed: IssueRow | null = null;
    for (const issue of issues) {
        const words = issueWords(s, issue.kind);
        const messages = issue.message ? [issue.message] : [];
        if (words) {
            rows.push({
                key: `kind:${issue.kind}`,
                label: words.short,
                explanation: fill(explanationFor(issue.kind, words, s, lastPushWasRestore), { provider }),
                messages,
            });
        } else if (unnamed) {
            unnamed.messages.push(...messages);
        } else {
            unnamed = {
                key: "unnamed",
                label: s.unknownIssue.short,
                explanation: s.unknownIssue.long,
                messages,
            };
            rows.push(unnamed);
        }
    }
    return rows;
}

/**
 * The sidebar's sync indicator: "everything is syncing and all is good —
 * unless it isn't".
 *
 * Self-fetching and silent when there is nothing to say: a property with no
 * active strategy is not in sync mode, so the rail shows nothing rather than
 * "all good" about a property nobody prices. The verdict is the server's
 * (`/api/sync-status`), so this and the Strategies page cannot disagree.
 *
 * The rail carries a state word over a relative time, and nothing else. It
 * does **not** name the provider: "In sync with Better Hotel" is 25
 * characters in a 16rem rail and 30 in Czech, so the one word that mattered
 * was the one the ellipsis ate — and the property switcher directly above
 * already says which property this is. The provider is named once in the
 * popover, where there is room.
 *
 * Where the page cannot read the state, the second line is what to do about
 * that instead of a time: a verdict it does not list (`unlistedVerdictReading`)
 * and a status it could not draw at all (`SyncStatusUnavailable`) both read
 * "Status unknown" over "Reload the page".
 *
 * Under the time stands one more line where the status carries tonight's
 * reading and the rail can vouch for it: what Optivo did with tonight's
 * prices, and the market on a day it is not ordinary ("Discounting today ·
 * Soft market"). It is the dashboard's reading of tonight in a few words, on
 * every page, and deliberately no block of its own: no mark, no colour, no rule. The
 * folded rail has no room for it and says the sentences in its tooltip; the
 * popover says them in a section of its own. When each is said is
 * `pms/tonightReading.ts`'s to decide, and a status without the reading (an
 * older server, a reading that could not be composed) leaves the indicator
 * exactly as it was.
 */
export function PmsSyncStatus({
    refreshKey,
    connectionId,
    collapsed = false,
    onNavigate,
    dashboardOpen = false,
}: PmsSyncStatusProps) {
    const status = useSyncStatus(refreshKey);
    const aboutAnotherProperty = status?.mode === "on" && connectionId != null && status.connectionId !== connectionId;

    // Asked again with every verdict that still names the other property,
    // until the shell's notice takes the rail's indicator away.
    useEffect(() => {
        if (aboutAnotherProperty) requestOpenPropertyCheck();
    }, [aboutAnotherProperty, status]);

    if (!status || status.mode !== "on" || aboutAnotherProperty) return null;

    // Split so the chip's own hooks run only once there is a status, and so
    // a property switch remounts it — the previous state a flash is measured
    // against belongs to the property that was on screen.
    //
    // Everything that reads what the status holds is drawn inside the
    // boundary; above it only the mode and the property are read, which any
    // answer survives. A status the chip cannot draw then costs the rail this
    // line and not the page, and the next status is tried in its place (the
    // key is the status itself, the boundary sitting under the hook that
    // holds it). The boundary is remounted with the chip, so a fault over one
    // property's status never stands over another's.
    return (
        <RenderFaultBoundary
            part="rail.syncStatus"
            retryKey={status}
            fallback={<SyncStatusUnavailable collapsed={collapsed} />}
        >
            <SyncStatusChip status={status} collapsed={collapsed} onNavigate={onNavigate} dashboardOpen={dashboardOpen} />
        </RenderFaultBoundary>
    );
}

/**
 * What the rail shows where the indicator could not be drawn.
 *
 * Not nothing: nothing is what the rail shows for a property nobody syncs,
 * and this one is priced and synced. Its status arrived and the page could not
 * draw it. So the line holds its place and says the one thing the page knows,
 * in the words it has for a verdict it cannot read, over what brings a page
 * that can where the fault is the likeliest one: a page older than the server.
 * The neutral mark claims neither a fault nor that all is well. There is no
 * popover behind it, the popover being part of what could not be drawn.
 *
 * It reads nothing off the status, so it cannot fail the way the chip did.
 */
function SyncStatusUnavailable({ collapsed }: { collapsed: boolean }) {
    const { t } = useLanguage();
    const { short, reload } = t.syncStatus.unknownState;

    if (collapsed) {
        const name = `${short} · ${reload}`;
        return (
            <div className="py-2 flex flex-col items-center border-b border-sidebar-border">
                <span
                    role="img"
                    data-sync-unavailable
                    aria-label={name}
                    title={name}
                    className="flex size-9 items-center justify-center"
                >
                    <StatusMark tone="muted" surface="rail" />
                </span>
            </div>
        );
    }

    return (
        <div className="px-3 py-2 border-b border-sidebar-border">
            <div
                data-sync-unavailable
                className="flex items-start gap-2 px-2 py-1.5 text-left text-sidebar-foreground"
            >
                <RailLines tone="muted" headline={short} subline={reload} />
            </div>
        </div>
    );
}

interface RailLinesProps {
    tone: Tone;
    headline: string;
    subline: string;
    /** A third line under the time: tonight's, where the chip has one to draw. */
    children?: ReactNode;
}

/** The expanded rail's line: the mark, the state and what stands under it. */
function RailLines({ tone, headline, subline, children }: RailLinesProps) {
    return (
        <>
            <span className="flex h-5 w-4 shrink-0 items-center justify-center">
                <StatusMark tone={tone} surface="rail" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm truncate">{headline}</span>
                <span className="block text-xs text-sidebar-foreground/70 truncate">{subline}</span>
                {children}
            </span>
        </>
    );
}

interface TonightProps {
    status: PmsSyncStatusOn;
    /** The moment the chip was drawn at, by which the age of the prices is read. */
    now: Date;
}

/**
 * Holds a fault in what is drawn of tonight to that, under the chip's own
 * boundary.
 *
 * The reading is one more answer from a server released apart from the page,
 * so everything that reads `status.tonight` (the rail's third line, the
 * tooltip's sentences, the popover's section) is drawn inside this. A reading
 * the page cannot draw then costs the reading, and the state, its time and the
 * popover's facts stay: the chip's boundary would have traded all of them for
 * "Status unknown" over a fault in a line the indicator did without for years.
 * Nothing stands in, as nothing stands there for a status with no reading.
 * Tried again with the next status, and reported under a part of its own, so
 * the report says what the user lost.
 *
 * The boundary draws no element of its own, so what it holds decides where it
 * may stand: a `span` inside the chip's button, a block in the popover.
 */
function TonightBoundary({ status, children }: { status: PmsSyncStatusOn; children: ReactNode }) {
    return (
        <RenderFaultBoundary part="rail.syncStatus.tonight" retryKey={status} fallback={null}>
            {children}
        </RenderFaultBoundary>
    );
}

/**
 * The open rail's third line.
 *
 * At the full strength of the rail's text, where the time above it is at 70 %:
 * of the two it is the one worth finding. It has no colour, dot, icon or rule
 * of its own, since the chip's one mark is the sync's.
 *
 * The pricing word is never cut. The market follows it with its separator
 * inside its own box, in a row one line high that wraps and clips: a market
 * that does not fit goes whole to a second row nobody sees, where an ellipsis
 * would have left "· Soft ma…". That is the net for a pair beyond the measured
 * envelope and not the layout: the words in `syncStatus.tonight` are chosen so
 * that every pair fits the line's box (215px at the 18px root).
 *
 * One line high by the line's own height (`h-lh`), not a length that is one
 * only while the font is exactly 0.75rem: a floored font size or the reader's
 * own text spacing would cut the descenders of the one word that is never
 * meant to be cut. And clipped (`overflow-clip`), not hidden: a hidden box is
 * a scroll container, and anything that scrolled the dropped market into view
 * would show it in the pricing word's place.
 */
function TonightRailLine({ status, now }: TonightProps) {
    const { t } = useLanguage();
    const line = tonightLine(status, t.syncStatus.tonight, now);
    if (!line) return null;

    return (
        <span data-sync-tonight className="flex h-lh flex-wrap overflow-clip text-xs text-sidebar-foreground">
            {"stale" in line ? (
                <span className="shrink-0 whitespace-nowrap">{line.stale}</span>
            ) : (
                <>
                    <span className="shrink-0 whitespace-nowrap">{line.pricing}</span>
                    {line.market && <span className="whitespace-pre">{` · ${line.market}`}</span>}
                </>
            )}
        </span>
    );
}

/** What the folded rail's tooltip says of tonight, a line each, under the state and its time. */
function TonightTooltipLines({ status, now }: TonightProps) {
    const { t } = useLanguage();
    return (
        <>
            {tonightTooltipLines(status, t, now).map((line, at) => (
                <span key={at} className="block">{line}</span>
            ))}
        </>
    );
}

interface SyncStatusChipProps {
    status: PmsSyncStatusOn;
    collapsed: boolean;
    onNavigate?: (page: SyncStatusDestination) => void;
    dashboardOpen: boolean;
}

function SyncStatusChip({ status, collapsed, onNavigate, dashboardOpen }: SyncStatusChipProps) {
    const { t, locale } = useLanguage();
    const [open, setOpen] = useState(false);
    // The folded rail's tooltip, which gives way to the popover.
    const [hintOpen, setHintOpen] = useState(false);
    // Set while the popover is handing the focus back to the button as it
    // closes, so that focus is not taken for a request to explain the dot.
    const focusHandedBack = useRef(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // A read-only viewer sees how the sync is doing and cannot configure it.
    const readOnly = useReadOnlyAccess();
    // Which side the popover opens on is decided by the room the chip has,
    // measured: beside the rail where 24rem fits to the right of it, under
    // the chip where it does not. On a phone the rail is the menu at 16rem,
    // and the popover opened to its right stood 215px off screen (242 under
    // high contrast); Radix flips only to the opposite side, where there is
    // no room either, and no viewport breakpoint can see the rail's width
    // (4rem folded, 16rem open, the menu at full width).
    const triggerRef = useRef<HTMLButtonElement>(null);
    const side = usePopoverSide(triggerRef, open, SYNC_POPOVER_ROOM);
    // Inside the phone's menu the chip stands in a dialog, whose scroll lock
    // takes the wheel and the finger of anything outside its panel, and the
    // popover is drawn outside it, at the end of the page: held to the
    // window's height there, it could not be scrolled at all (measured in
    // Chromium, 5 Oct 2026). So there it is a modal layer of its own, with a
    // scroll lock that stands on top of the menu's, taking the menu out of
    // reach while it is open as the menu takes the page. Beside the page it
    // stays the plain popover it was: a click on the rail still reaches it.
    const insideMenu = useInsidePhoneMenu();

    const now = new Date();
    const s = t.syncStatus;
    const providers = (t.pms as { providers?: Record<string, { displayName?: string }> }).providers;
    const provider = providers?.[status.providerType]?.displayName ?? "PMS";
    const rel = (iso: string) => formatRelativeTime(iso, locale, now);
    const reading = readingFor(status, s, rel);
    // Keyed on what the rail shows, so the minute-by-minute march of the
    // relative time underneath does not set it off — plus the cadence, which
    // shows in the popover rather than on the rail and is the one fact here
    // the operator cannot set themselves: support moves it from the admin
    // console, so without this a 2-hour-to-30-minute change lands in silence
    // on the screen of the person it actually affects.
    //
    // Tonight's line is deliberately not part of it. The flash is how the
    // rail says the sync state changed, which is rare and worth an eye. What
    // Optivo did with the prices moves from holding to discounting and back
    // several times a day, and a rail that lit up for each would teach the
    // operator to ignore the one light that means something.
    const flashing = useChangeFlash(`${reading.tone}|${reading.headline}|${status.syncFrequencyMinutes}`);
    const railClass = `w-full rounded-md transition-colors ${flashing ? "flash-attention" : ""}`;

    // The tooltip is put away with every move of the popover, not only kept
    // from showing while it is open. A pointer that arrives and presses within
    // one frame asks for the tooltip and then for the popover before the
    // tooltip is drawn; the press finds none to close, and the request stayed
    // pending behind the popover and came up as it closed (measured in
    // Chromium).
    const movePopover = (next: boolean) => {
        setOpen(next);
        setHintOpen(false);
    };

    // The popover hands the focus back to the button as it closes on Escape
    // or on one of its actions, and the tooltip opens on focus: dismissing one
    // answer brought up the other in its place. That focus is the popover's
    // doing and nobody asking what the dot means, so the tooltip stays shut
    // for it, and opens again on the next focus or hover. The flag is taken
    // back within the task, since a popover closed by a press elsewhere hands
    // no focus back.
    const noteFocusHandedBack = () => {
        focusHandedBack.current = true;
        window.setTimeout(() => {
            focusHandedBack.current = false;
        }, 0);
    };

    const navigate = (page: SyncStatusDestination) => {
        setOpen(false);
        onNavigate?.(page);
    };

    const openSettings = () => {
        setOpen(false);
        setSettingsOpen(true);
    };

    const details = (
        <SyncStatusDetails
            status={status}
            reading={reading}
            provider={provider}
            rel={rel}
            now={now}
            side={side}
            onNavigate={navigate}
            dashboardOpen={dashboardOpen}
            onOpenSettings={readOnly ? undefined : openSettings}
            onCloseAutoFocus={noteFocusHandedBack}
        />
    );

    {/* Mounted only once asked for: it fetches a schedule and a cadence of
        its own, and the rail is on every page. */}
    const settings = settingsOpen && !readOnly && (
        <AutoSyncConfig
            connectionId={status.connectionId}
            open
            onOpenChange={setSettingsOpen}
        />
    );

    if (collapsed) {
        return (
            <div className="py-2 flex flex-col items-center border-b border-sidebar-border">
                <Popover open={open} onOpenChange={movePopover} modal={insideMenu}>
                    {/* The folded rail is a dot, and what it stands for is said
                        in a tooltip, as a folded destination's name is: on
                        hover and on keyboard focus, which the native title it
                        replaces never opened on, and laid out line by line,
                        which a title cannot be. It adds no mark to the rail:
                        the state and its time, then what the open rail's
                        third line would say, as sentences.

                        Never while the popover is open, which says all of it
                        and more, in the same place. The popover's trigger is
                        the outer of the two, so the button's `data-state` and
                        `aria-expanded` stay the popover's own. */}
                    <Tooltip open={hintOpen && !open} onOpenChange={(next) => setHintOpen(next && !open)}>
                        <PopoverTrigger asChild>
                            <TooltipTrigger asChild>
                                <Button
                                    ref={triggerRef}
                                    variant="ghost"
                                    size="icon"
                                    data-testid="sync-rail-button"
                                    aria-label={reading.headline}
                                    // Runs ahead of the tooltip's own handler,
                                    // which stands down for a focus marked
                                    // as dealt with.
                                    onFocus={(event) => {
                                        if (focusHandedBack.current) event.preventDefault();
                                    }}
                                    className={`text-sidebar-foreground hover:bg-sidebar-accent ${flashing ? "flash-attention" : ""}`}
                                >
                                    <StatusMark tone={reading.tone} surface="rail" />
                                </Button>
                            </TooltipTrigger>
                        </PopoverTrigger>
                        <TooltipContent
                            side="right"
                            className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]"
                        >
                            <span className="block">{reading.headline} · {reading.subline}</span>
                            <TonightBoundary status={status}>
                                <TonightTooltipLines status={status} now={now} />
                            </TonightBoundary>
                        </TooltipContent>
                    </Tooltip>
                    {details}
                </Popover>
                {settings}
            </div>
        );
    }

    return (
        <div className="px-3 py-2 border-b border-sidebar-border">
            <Popover open={open} onOpenChange={setOpen} modal={insideMenu}>
                <PopoverTrigger asChild>
                    <button
                        ref={triggerRef}
                        type="button"
                        data-sync-rail
                        data-testid="sync-rail-button"
                        className={`flex items-start gap-2 px-2 py-1.5 hover:bg-sidebar-accent text-sidebar-foreground text-left ${railClass}`}
                    >
                        <RailLines tone={reading.tone} headline={reading.headline} subline={reading.subline}>
                            <TonightBoundary status={status}>
                                <TonightRailLine status={status} now={now} />
                            </TonightBoundary>
                        </RailLines>
                    </button>
                </PopoverTrigger>
                {details}
            </Popover>
            {settings}
        </div>
    );
}

/**
 * The verdict and the auto-send switch, read together.
 *
 * A fault leads with itself — "Last run failed" says in three words what a
 * generic "Sync needs attention" took five to say without saying anything —
 * and the relative time drops beneath it. And `good` with auto-send off is
 * **not** "In sync": the bookings still come in, but no price ever leaves,
 * so the claim about the PMS would be untrue. It is a state the operator
 * chose, not a fault, so it takes the neutral mark rather than a warning.
 *
 * `good` has a branch of its own, like every verdict this build lists: the
 * page says "In sync" where the server said `good`, never where no other
 * branch happened to match (see `unlistedVerdictReading`).
 */
function readingFor(status: PmsSyncStatusOn, s: Strings, rel: (iso: string) => string): Reading {
    const checked = status.lastRunFinishedAt ? rel(status.lastRunFinishedAt) : s.notYet;
    const { verdict } = status;

    switch (verdict) {
        case "syncing":
            return { headline: s.state.syncing, subline: checked, summary: s.summary.syncing, tone: "busy" };

        case "warning":
        case "error": {
            // Where this page has no name for the leading issue, the headline
            // is the generic state, not the next issue it can name. The server
            // lists the most severe first and the mark takes its colour from
            // the verdict, so "Behind schedule" on the error mark would pass a
            // lesser fault off as the one that made it an error, and read as
            // the whole of what is wrong. The popover would be misled with it:
            // its header repeats this headline, and its list leaves the first
            // issue unnamed on the strength of that. A verdict that names no
            // issue at all reads the same way, never as "In sync", the one
            // claim it rules out.
            const leading = status.issues.length > 0 ? issueWords(s, status.issues[0].kind) : null;
            return {
                headline: (leading ?? s.unknownIssue).short,
                subline: checked,
                summary: "",
                tone: verdict === "error" ? "bad" : "warn",
                namesLeadingIssue: true,
            };
        }

        case "pending":
            return {
                headline: s.state.pending,
                subline: status.nextRunAt ? rel(status.nextRunAt) : s.paused,
                summary: s.summary.pending,
                tone: "muted",
            };

        case "good":
            return status.push.autoSyncEnabled
                ? { headline: s.state.good, subline: checked, summary: s.summary.good, tone: "ok" }
                : { headline: s.state.manual, subline: checked, summary: s.summary.manual, tone: "muted" };

        default:
            return unlistedVerdictReading(verdict, s);
    }
}

/**
 * What the page says of a verdict it does not list.
 *
 * `SyncVerdict` lists the verdicts this page was built with, which is not what
 * the server may send: the two deploy separately and a tab stays open for
 * days, so a verdict the server gains is met by pages that predate it, as an
 * issue kind is. Such a verdict used to fall through to `good` and read "In
 * sync" on the success mark (or "Manual sending"): the one claim the indicator
 * makes only where it can prove it, made on no evidence at all.
 *
 * Of such a verdict the page knows neither whether it is a fault nor how
 * severe. So it takes neither side. "Needs attention" on the caution mark
 * would call a fault what may be none (a sync the operator paused, say), just
 * as "In sync" called it fine. It reads "Status unknown" on the neutral mark,
 * and the line under it says what brings the page that can read the verdict,
 * not when the last run finished: a time there dates the state ("unknown
 * since"), and a finished run stated under a verdict nobody read is the
 * reassurance the page has no grounds for. Nor does an issue the response
 * lists lead: a verdict can outrank its issues, as `syncing` does. They are
 * listed in the popover, each by name, beside what the server recorded
 * (bookings in, prices out, next run), which is true whatever the verdict
 * means.
 *
 * The parameter is `never` on purpose. Every verdict the page lists has its
 * own branch in `readingFor`, and the compiler refuses one added to the list
 * without; only a value off the wire reaches this.
 */
function unlistedVerdictReading(_verdict: never, s: Strings): Reading {
    return {
        headline: s.unknownState.short,
        subline: s.unknownState.reload,
        summary: s.unknownState.long,
        tone: "muted",
    };
}

function StatusMark({ tone, surface }: { tone: Tone; surface: MarkSurface }) {
    if (tone === "busy") {
        return (
            <RefreshCw
                data-sync-spinner
                aria-hidden
                className={`size-4 animate-spin motion-reduce:animate-none ${NEUTRAL_CLASS[surface].spinner}`}
            />
        );
    }
    const color = tone === "muted" ? NEUTRAL_CLASS[surface].dot : DOT_CLASS[tone];
    return (
        <span data-sync-dot aria-hidden className={`inline-block h-2 w-2 rounded-full ${color}`} />
    );
}

interface SyncStatusDetailsProps {
    status: PmsSyncStatusOn;
    reading: Reading;
    provider: string;
    rel: (iso: string) => string;
    /** The moment the chip was drawn at, by which the age of tonight's prices is read. */
    now: Date;
    /** Beside the chip where the popover fits there, under it where it does not. */
    side: PopoverSide;
    onNavigate: (page: SyncStatusDestination) => void;
    /** Whether the dashboard is the page behind the popover, which then offers no way to it. */
    dashboardOpen: boolean;
    /** Open the auto-sync settings. Omitted for a read-only viewer, who changes nothing. */
    onOpenSettings?: () => void;
    /** Told as the popover closes, just before it hands the focus back to the chip. */
    onCloseAutoFocus: () => void;
}

/**
 * The facts, as the two directions they actually are: bookings come in from
 * the PMS, prices go back out to it. Each carries its own mark, so "the
 * reading works but the sending does not" — the shape of an auto-send that
 * is off, and of a push that failed — is one glance rather than a paragraph.
 */
function SyncStatusDetails({
    status,
    reading,
    provider,
    rel,
    now,
    side,
    onNavigate,
    dashboardOpen,
    onOpenSettings,
    onCloseAutoFocus,
}: SyncStatusDetailsProps) {
    const { t } = useLanguage();
    const s = t.syncStatus;
    // The way on from the "Today" section, so offered where the status
    // carries the reading that section is drawn from and the dashboard is not
    // already the page behind the popover. Whether there is a reading is all
    // that is asked of it out here; what it holds is read inside the
    // section's own boundary.
    const leadsToDashboard = !dashboardOpen && readingOf(status.tonight) !== null;
    const needsConnection = status.issues.some((issue) => CONNECTION_ISSUES.has(issue.kind));
    // Both lead to the Upload Log: a send that failed, and one Optivo itself
    // held part of back, whose row there names each night and why.
    // Not offered to the front desk, whose Prices page is the reservations
    // grid alone: the fault is still said above, and the Upload Log is the
    // revenue manager's to open.
    const frontDesk = useFrontDeskAccess();
    const pushNeedsLog = !frontDesk && status.issues.some(
        (issue) => issue.kind === "lastPushFailed" || issue.kind === "lastPushHeldBack",
    );
    // Cells of the last send Optivo held back, where that is all it was short
    // of. Its row is stored as failed, since not every cell landed, and it is
    // no failure: the outgoing direction takes the caution mark and says how
    // many, never the failed one. Absent from a server that predates it.
    const heldBack = status.push.lastHeldBack ?? 0;
    const cadence = syncCadencePhrase(status.syncFrequencyMinutes, t);
    // Whether the last upload put a price list back, by which a night it held
    // back is worded. Only the server's own yes counts: a status from a
    // server that predates the statement, and one whose row does not say, are
    // read as a send's, as every status was read.
    const lastPushWasRestore = status.push.lastRestoresPriceList === true;
    const rows = issueRows(status.issues, s, provider, lastPushWasRestore);
    /** Whether the list prints an entry's name, or the header already said it. */
    const named = (index: number) => index > 0 || !reading.namesLeadingIssue;

    const inboundTone: Tone = status.issues.some((issue) => issue.kind === "lastRunFailed")
        ? "bad"
        : status.lastRunFinishedAt
            ? "ok"
            : "muted";
    const outboundTone: Tone = heldBack > 0
        ? "warn"
        : status.push.lastStatus === "failed"
            ? "bad"
            : status.push.autoSyncEnabled && status.push.lastAt
                ? "ok"
                : "muted";

    const outboundValue = [
        status.push.lastAt
            ? `${rel(status.push.lastAt)} · ${fill(s.prices, { count: String(status.push.lastEntries) })}`
            : s.notYet,
        heldBack > 0 ? fill(s.heldBack, { count: String(heldBack) }) : null,
        status.push.autoSyncEnabled ? null : s.manualTag,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        // Aligned to the chip's start on either side, so under the chip its
        // left edge is the rail's own. No taller than the room Radix measures
        // on the side it lands on, scrolling inside: on a phone held sideways
        // (320px tall) it ran 20 to 69px past the bottom with nothing to
        // scroll it by, and Radix flips a popover taller than both sides to
        // the one with more room.
        <PopoverContent
            side={side}
            align="start"
            sideOffset={SYNC_POPOVER_ROOM.sideOffset}
            collisionPadding={SYNC_POPOVER_ROOM.collisionPadding}
            onCloseAutoFocus={onCloseAutoFocus}
            className="w-96 max-w-[calc(100vw-2rem)] max-h-(--radix-popover-content-available-height) overflow-y-auto overflow-x-hidden p-0"
        >
            <div className="border-b border-border px-4 py-3">
                <p className="flex items-center gap-2 text-sm font-medium">
                    <span className="flex h-5 w-4 shrink-0 items-center justify-center">
                        <StatusMark tone={reading.tone} surface="popover" />
                    </span>
                    <span>{reading.headline}</span>
                </p>
                <p className="mt-0.5 pl-6 text-xs text-muted-foreground">
                    {provider} · {cadence}
                </p>
                {reading.summary && (
                    <p className="mt-2 text-xs text-muted-foreground">{reading.summary}</p>
                )}
            </div>

            {rows.length > 0 && (
                <ul data-testid="sync-issues" className="space-y-3 border-b border-border px-4 py-3">
                    {rows.map((row, index) => (
                        <li key={row.key} className="text-xs">
                            {/* Where the header leads with the first fault,
                                naming it again here is three words of nothing
                                in a narrow panel. A second fault has nowhere
                                else to be named, so it keeps its label, and so
                                does the first where the header leads with a
                                state instead: a run in flight, or a verdict
                                the page cannot read. */}
                            {named(index) && (
                                <p className="text-xs font-medium">{row.label}</p>
                            )}
                            {/* Sized explicitly rather than by inheritance:
                                globals.css gives a bare <p> the base 18px
                                unless the container holds a class matching
                                " text-", so inheriting here is one sibling's
                                class string away from breaking. */}
                            <p className={`text-xs text-muted-foreground ${named(index) ? "mt-0.5" : ""}`}>
                                {row.explanation}
                            </p>
                            {row.messages.map((message, at) => (
                                <p
                                    key={at}
                                    className="mt-1 break-words font-mono text-[11px] leading-snug text-muted-foreground"
                                >
                                    {message}
                                </p>
                            ))}
                        </li>
                    ))}
                </ul>
            )}

            <div className="space-y-2 px-4 py-3">
                <FlowRow
                    testId="flow-in"
                    Icon={ArrowDownToLine}
                    label={s.flow.inbound}
                    value={status.lastRunFinishedAt ? rel(status.lastRunFinishedAt) : s.notYet}
                    tone={inboundTone}
                />
                <FlowRow
                    testId="flow-out"
                    Icon={ArrowUpFromLine}
                    label={s.flow.outbound}
                    value={outboundValue}
                    tone={outboundTone}
                />
                <FlowRow
                    testId="flow-next"
                    Icon={Timer}
                    label={s.flow.next}
                    value={status.nextRunAt ? rel(status.nextRunAt) : s.paused}
                />
            </div>

            <TonightBoundary status={status}>
                <TonightSection status={status} now={now} />
            </TonightBoundary>

            {/* 2.5rem below `sm`, where the rail is the phone's menu and the
                root is 16px: `size="sm"` alone is 32px there, under the 40 a
                finger is given everywhere else on the rail. */}
            <div className="flex flex-wrap gap-2 border-t border-border px-4 py-2">
                {needsConnection && (
                    <Button size="sm" variant="outline" className={POPOVER_ACTION_CLASS} onClick={() => onNavigate("connections")}>
                        {s.actions.checkConnection}
                    </Button>
                )}
                {pushNeedsLog && (
                    <Button size="sm" variant="outline" className={POPOVER_ACTION_CLASS} onClick={() => onNavigate("uploadLog")}>
                        {s.actions.uploadLog}
                    </Button>
                )}
                {onOpenSettings && (
                    <Button size="sm" variant="ghost" className={POPOVER_ACTION_CLASS} onClick={onOpenSettings}>
                        {s.actions.syncSettings}
                    </Button>
                )}
                {leadsToDashboard && (
                    <Button size="sm" variant="ghost" className={POPOVER_ACTION_CLASS} onClick={() => onNavigate("dashboard")}>
                        {t.sidebar.dashboard}
                    </Button>
                )}
            </div>
        </PopoverContent>
    );
}

/**
 * The popover's "Today" section: tonight's prices and market in the
 * dashboard's own sentences, each under the dashboard's own mark.
 *
 * Drawn wherever the status carries a reading, whatever the verdict. The rail
 * says a few words and only where it can vouch for them; here there is room
 * to be exact, so what would have kept the line off the rail is said instead:
 * that the prices are over a day old, in the caution the dashboard's nights
 * give it, and that they do not leave for the PMS on their own. A value the
 * page has no sentence for is worded as no reading, on the hollow mark, as
 * the dashboard words it.
 *
 * The caution is the sentence in the text colour behind a triangle in the
 * warning one. A 13.5px sentence in `text-caution` is 2.2:1 on the popover in
 * the light Previo and Better Hotel skins; the triangle needs 3:1 and has it
 * in every skin, and it stands in the one-rem box the marks above it stand
 * in, so the sentence starts where theirs do.
 *
 * Every line is sized explicitly, as the issue list above is: globals.css
 * gives a bare <p> the base 18px.
 */
function TonightSection({ status, now }: TonightProps) {
    const { t } = useLanguage();
    const titleId = useId();
    const tonight = readingOf(status.tonight);
    if (!tonight) return null;

    const { pricing, market, stale, manual } = tonightSentences(tonight, t, now);
    if (!pricing && !market && !stale && !manual) return null;
    const title = tonightTitle(t);

    return (
        <div
            role="group"
            aria-labelledby={title ? titleId : undefined}
            data-testid="sync-tonight"
            className="space-y-2 border-t border-border px-4 py-3"
        >
            {title && (
                <p id={titleId} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {title}
                </p>
            )}
            {pricing && (
                <p className="flex items-start gap-2 text-sm text-foreground">
                    <PricingMark action={pricing.action} className="mt-0.5" />
                    <span className="min-w-0">{pricing.sentence}</span>
                </p>
            )}
            {market && (
                <div>
                    <p className="flex items-start gap-2 text-sm text-foreground">
                        <OutlookMark tone={market.tone} className="mt-0.5" />
                        <span className="min-w-0">{market.sentence}</span>
                    </p>
                    {market.drivers.length > 0 && (
                        <p className="mt-0.5 pl-6 text-xs text-muted-foreground">{market.drivers.join(" · ")}</p>
                    )}
                </div>
            )}
            {stale && (
                <p className="flex items-start gap-2 text-xs text-foreground">
                    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
                        <AlertTriangle className="size-3.5 text-warning" />
                    </span>
                    <span className="min-w-0">{stale}</span>
                </p>
            )}
            {manual && <p className="text-xs text-muted-foreground">{manual}</p>}
        </div>
    );
}

interface FlowRowProps {
    testId: string;
    Icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    label: string;
    value: ReactNode;
    /** Omitted for the row that is a schedule rather than a direction. */
    tone?: Tone;
}

function FlowRow({ testId, Icon, label, value, tone }: FlowRowProps) {
    return (
        <div data-testid={testId} className="flex items-center gap-2 text-xs">
            <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="text-muted-foreground">{label}</span>
            <span className="ml-auto flex items-center gap-2 font-mono text-foreground">
                {value}
                {tone && (
                    <span
                        data-flow-dot
                        aria-hidden
                        className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                            tone === "muted" ? NEUTRAL_CLASS.popover.dot : DOT_CLASS[tone as "ok" | "warn" | "bad"]
                        }`}
                    />
                )}
            </span>
        </div>
    );
}
